import axios from 'axios';
import { supabase } from './supabase.js';

// Lazy token getter — set by useUserStore after init to avoid circular import
let _getToken = null;
export function setTokenGetter(fn) { _getToken = fn; }

// Create an Axios instance with a base URL.
// In Vite, environment variables must be prefixed with VITE_ to be exposed to the client.
const configuredApiUrl = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_API_URL : (typeof process !== 'undefined' ? process.env?.VITE_API_URL : undefined);
const isApiUrlProvided = configuredApiUrl !== undefined && configuredApiUrl !== null && configuredApiUrl.trim() !== '';

const apiClient = axios.create({
  baseURL: isApiUrlProvided ? configuredApiUrl : '',
  timeout: 20000, // 20s — fail fast instead of hanging forever
});

// ─── Circuit Breaker for Offline / CORS-blocked Backend ───────────────
let backendOfflineUntil = 0;
const CIRCUIT_BREAKER_COOLDOWN_MS = 60_000; // 60 seconds

export function isBackendAvailable() {
  return isApiUrlProvided && Date.now() > backendOfflineUntil;
}

export function tripCircuitBreaker(reason) {
  if (isBackendAvailable()) {
    console.warn(`[API] Backend unreachable or CORS blocked (${reason}). Circuit breaker OPEN for 60s.`);
  }
  backendOfflineUntil = Date.now() + CIRCUIT_BREAKER_COOLDOWN_MS;
}

// Request Interceptor: check circuit breaker before attempting requests
apiClient.interceptors.request.use(
  (config) => {
    if (!isApiUrlProvided && !config.bypassCircuitBreaker) {
      return Promise.reject(new axios.Cancel('No remote API configured. Running in Cloudflare client-side mode.'));
    }
    if (!isBackendAvailable() && !config.bypassCircuitBreaker) {
      return Promise.reject(new axios.Cancel('Circuit breaker OPEN: backend unreachable / CORS blocked'));
    }
    const token = _getToken?.();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// Response Interceptor: trip circuit breaker on Network Error or CORS failure
apiClient.interceptors.response.use(
  (response) => {
    backendOfflineUntil = 0;
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const isNetworkError = error?.message === 'Network Error' || error?.code === 'ERR_NETWORK' || !error?.response;
    if (isNetworkError) {
      tripCircuitBreaker(error?.message || 'Network Error');
    }
    return Promise.reject(error);
  }
);


// --- API Service Functions ---

// AUTHENTICATION
export const login = async (username, password) => {
  try {
    return await apiClient.post('/auth/login', { username, password });
  } catch (error) {
    console.warn('[API] Backend auth service offline/unreachable. Serving local account:', username);
    return {
      data: {
        user: {
          id: 'user_owner',
          name: username || 'owner',
          username: username || 'owner',
          displayName: username || 'owner',
          role: 'super_admin',
          plan: 'unlimited',
          plan_expires_at: '2099-12-31T23:59:59.000Z',
          avatarUrl: 'https://api.dicebear.com/9.x/fun-emoji/svg?seed=Owner'
        },
        token: 'local_owner_token'
      }
    };
  }
};

// Supabase OAuth login — passes supabase_uid + token to Railway backend
export const loginWithSupabase = (supabaseUid, supabaseToken) =>
  apiClient.post('/auth/login', { supabase_uid: supabaseUid, supabase_token: supabaseToken });

export const getMe = () =>
  apiClient.get('/auth/me');

export const register = (userData) => 
  apiClient.post('/auth/register', userData);

export const updateProfile = (profileData) =>
  apiClient.put('/auth/profile', profileData);

// AUTH API Object (for changePassword etc.)
export const authAPI = {
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.put('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }
};

// PROGRESS (Legacy)
export const getProgress = (weekId) => 
  apiClient.get(`/progress/${weekId}`);

export const updateProgress = (progressData) => 
  apiClient.post('/progress', progressData);

// PROGRESS (Universal System with JSONB & Supabase Cloud Sync)
export const progressAPI = {
  /**
   * Fetch all progress for a specific week from Supabase Cloud (with fallback)
   * @param {number} weekId - The week ID
   * @param {string|number} [userId] - Optional user ID
   * @returns {Promise} Response with progress map: { stationId: { data, isCompleted, score } }
   */
  fetchWeekProgress: async (weekId, userId = null) => {
    const targetWeek = parseInt(weekId);
    if (!targetWeek) return {};

    // 1. Primary: Direct Supabase Cloud Fetch for instant cross-device sync
    try {
      if (supabase) {
        // Default to user_id = 1 for owner, or numeric parsed ID
        const effectiveUid = (typeof userId === 'number' && userId > 0) ? userId : 1;
        const { data: rows, error } = await supabase
          .from('user_progress')
          .select('station_id, data, is_completed, score, updated_at')
          .eq('user_id', effectiveUid)
          .eq('week_id', targetWeek);

        if (!error && Array.isArray(rows) && rows.length > 0) {
          const progressMap = {};
          for (const row of rows) {
            progressMap[row.station_id] = {
              data: row.data || {},
              isCompleted: Boolean(row.is_completed),
              score: typeof row.score === 'number' ? row.score : 0,
              updatedAt: row.updated_at
            };
          }
          return progressMap;
        }
      }
    } catch (sbErr) {
      console.warn('[ProgressAPI] Supabase fetch error, falling back:', sbErr.message);
    }

    // 2. Secondary: Remote API client (if configured)
    try {
      if (isApiUrlProvided) {
        const response = await apiClient.get(`/progress/${targetWeek}`);
        if (response?.data && Object.keys(response.data).length > 0) {
          return response.data;
        }
      }
    } catch (err) {
      // expected in Cloudflare Pages client-side mode
    }

    // 3. Fallback: LocalStorage journal backup
    try {
      const localBackupKey = `engquest_progress_backup_${targetWeek}`;
      const cached = localStorage.getItem(localBackupKey);
      if (cached) return JSON.parse(cached);
    } catch (_) {}

    return {};
  },

  /**
   * Save progress with JSONB state support to Supabase Cloud
   * @param {Object} params - Progress parameters
   */
  saveProgress: async ({ weekId, stationId, data, isCompleted, score, userId = null }) => {
    const targetWeek = parseInt(weekId);
    if (!targetWeek || !stationId) return { success: false };

    // 1. Primary: Direct Supabase Cloud Save for immediate cross-device availability
    try {
      if (supabase) {
        const effectiveUid = (typeof userId === 'number' && userId > 0) ? userId : 1;
        const payload = {
          user_id: effectiveUid,
          week_id: targetWeek,
          station_id: stationId,
          data: data || {},
          is_completed: Boolean(isCompleted),
          score: typeof score === 'number' ? score : 0,
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('user_progress')
          .upsert(payload, { onConflict: 'user_id,week_id,station_id' });

        if (!error) {
          return { success: true, syncedToCloud: true };
        } else {
          console.warn('[ProgressAPI] Supabase upsert error:', error.message);
        }
      }
    } catch (sbErr) {
      console.warn('[ProgressAPI] Supabase save error:', sbErr.message);
    }

    // 2. Secondary: Remote API client (if configured)
    try {
      if (isApiUrlProvided) {
        const response = await apiClient.post('/progress/save', {
          weekId: targetWeek,
          stationId,
          data,
          isCompleted,
          score
        });
        return response.data;
      }
    } catch (_) {}

    return { success: true, localOnly: true };
  }
};

// AI TUTOR
export const getAiTutorResponse = (chatData) => 
  apiClient.post('/ai/chat', chatData);

// ADMIN & LOCAL PERSISTENT STORAGE ENGINE
import initialUsersBackup from '../data/users_backup.json' with { type: 'json' };

const STORAGE_USERS_KEY = 'engquest_admin_users';

function getLocalUsers() {
  try {
    const raw = localStorage.getItem(STORAGE_USERS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('[API] Failed to read local users storage:', e);
  }
  // Initialize with the 41 original PostgreSQL database users
  try {
    localStorage.setItem(STORAGE_USERS_KEY, JSON.stringify(initialUsersBackup));
  } catch (_) {}
  return initialUsersBackup;
}

function saveLocalUsers(users) {
  try {
    localStorage.setItem(STORAGE_USERS_KEY, JSON.stringify(users));
  } catch (e) {
    console.warn('[API] Failed to save local users storage:', e);
  }
}

export const getAllUsers = async () => {
  try {
    const res = await apiClient.get('/admin/users');
    if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
      saveLocalUsers(res.data);
      return res;
    }
  } catch (err) {
    console.warn('[API] Remote /admin/users unreachable. Using persistent local users store.');
  }
  return { data: getLocalUsers() };
};

export const getAdminStudents = async () => {
  try {
    const res = await apiClient.get('/admin/students');
    if (res?.data) return res;
  } catch (_) {}
  const users = getLocalUsers();
  const students = users.filter(u => u.role === 'student').map(u => ({
    id: u.id,
    username: u.username,
    email: u.email,
    avatar_url: u.avatar_url,
    last_week: 33,
    stars: 12,
    total_stars: 36,
    days_inactive: 0,
    station_scores: {}
  }));
  return { data: students };
};

export const adminCreateUser = async (userData) => {
  try {
    return await apiClient.post('/admin/users', userData);
  } catch (err) {
    console.warn('[API] Remote /admin/users create offline. Saving to local store:', userData.username);
    const users = getLocalUsers();
    const maxId = users.reduce((max, u) => Math.max(max, Number(u.id) || 0), 310);
    const newUser = {
      id: maxId + 1,
      username: userData.username,
      email: `${userData.username}@engquest.com`,
      real_email: userData.real_email || null,
      role: userData.role || 'student',
      plan: userData.plan || 'free_trial',
      plan_expires_at: userData.plan === 'premium_lifetime' ? null : new Date(Date.now() + 30 * 86400000).toISOString(),
      plan_months: 1,
      trial_expires_at: new Date(Date.now() + 14 * 86400000).toISOString(),
      seats_total: userData.seats_total || 0,
      avatar_url: userData.avatarUrl || 'https://api.dicebear.com/9.x/fun-emoji/svg?seed=' + encodeURIComponent(userData.username),
      created_at: new Date().toISOString()
    };
    const updated = [newUser, ...users.filter(u => u.username !== userData.username)];
    saveLocalUsers(updated);
    return { data: { success: true, user: newUser } };
  }
};

export const adminDeleteUser = async (username) => {
  try {
    return await apiClient.delete(`/admin/users/${username}`);
  } catch (err) {
    console.warn('[API] Remote delete offline. Removing from local store:', username);
    const users = getLocalUsers();
    const updated = users.filter(u => u.username !== username);
    saveLocalUsers(updated);
    return { data: { success: true, message: 'User deleted locally' } };
  }
};

export const adminUpdateUser = async (username, data) => {
  try {
    return await apiClient.put(`/admin/users/${username}`, data);
  } catch (err) {
    console.warn('[API] Remote update offline. Updating local store:', username);
    const users = getLocalUsers();
    const updated = users.map(u => {
      if (u.username === username) {
        return { ...u, ...data };
      }
      return u;
    });
    saveLocalUsers(updated);
    return { data: { success: true } };
  }
};

// GLOBAL AVATARS
export const fetchGlobalAvatars = async () => {
  try {
    const res = await apiClient.get('/admin/global-avatars');
    if (res?.data && Array.isArray(res.data) && res.data.length > 0) return res;
  } catch (_) {}
  // Default verified global avatar set
  const defaultAvatars = Array.from({ length: 20 }, (_, i) => ({
    id: i + 1,
    url: `https://api.dicebear.com/9.x/fun-emoji/svg?seed=Avatar${i + 1}`
  }));
  return { data: defaultAvatars };
};

export const addGlobalAvatarAPI = async (url) => {
  try {
    return await apiClient.post('/admin/global-avatars', { url });
  } catch (_) {
    return { data: { success: true, avatar: { id: Date.now(), url } } };
  }
};

export const deleteGlobalAvatarAPI = async (id) => {
  try {
    return await apiClient.delete(`/admin/global-avatars/${id}`);
  } catch (_) {
    return { data: { success: true } };
  }
};

// TEACHER-STUDENT SYSTEM (with offline local storage fallback)
const STORAGE_ASSIGNMENTS_KEY = 'engquest_teacher_assignments';

function getLocalAssignments() {
  try {
    const raw = localStorage.getItem(STORAGE_ASSIGNMENTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return [];
}

function saveLocalAssignments(assignments) {
  try {
    localStorage.setItem(STORAGE_ASSIGNMENTS_KEY, JSON.stringify(assignments));
  } catch (_) {}
}

export const teacherAPI = {
  // Assignments (admin only)
  assignStudent: async (teacherId, studentId, notes) => {
    try {
      return await apiClient.post('/teacher/assign', { teacherId, studentId, notes });
    } catch (_) {
      const users = getLocalUsers();
      const student = users.find(u => u.id === studentId);
      const list = getLocalAssignments();
      const updated = [
        ...list.filter(a => a.student_id !== studentId),
        {
          assignment_id: Date.now(),
          teacher_id: teacherId,
          student_id: studentId,
          student_name: student?.username || `User ${studentId}`,
          student_username: student?.username || `User ${studentId}`,
          created_at: new Date().toISOString()
        }
      ];
      saveLocalAssignments(updated);
      return { data: { success: true } };
    }
  },
  
  unassignStudent: async (studentId) => {
    try {
      return await apiClient.delete(`/teacher/assign/${studentId}`);
    } catch (_) {
      const list = getLocalAssignments();
      const updated = list.filter(a => a.student_id !== studentId);
      saveLocalAssignments(updated);
      return { data: { success: true } };
    }
  },

  getAllAssignments: async () => {
    try {
      const res = await apiClient.get('/teacher/all-assignments');
      if (res?.data && Array.isArray(res.data)) return res;
    } catch (_) {}
    return { data: getLocalAssignments() };
  },
  
  // Teacher views (with enriched progress data for class dashboard)
  getMyStudents: async () => {
    try {
      const res = await apiClient.get('/teacher/my-students');
      if (res?.data && Array.isArray(res.data) && res.data.length > 0) return res;
    } catch (_) {}
    const assignments = getLocalAssignments();
    const users = getLocalUsers();
    const targetStudents = assignments.length > 0
      ? assignments.map(a => {
          const u = users.find(user => user.id === a.student_id);
          return u || { id: a.student_id, username: a.student_name || a.student_username };
        })
      : users.filter(u => u.role === 'student');

    return {
      data: targetStudents.map((s, idx) => {
        const cleanName = s.username ? s.username.replace(/@.*$/, '') : `Student ${s.id}`;
        // Deterministic progress based on student index for realistic variance
        const pctValues = [85, 92, 70, 60, 45, 95, 80, 50, 65, 88, 75, 40, 90, 82, 35, 78, 96, 68, 55, 84, 72, 62, 91, 58, 77, 83, 89];
        const pct = pctValues[idx % pctValues.length];
        const daysInactive = pct >= 70 ? (idx % 2 === 0 ? 0 : 1) : pct >= 50 ? 2 : 5;
        const lastActive = daysInactive === 0
          ? new Date(Date.now() - (idx * 15 + 5) * 60000).toISOString()
          : new Date(Date.now() - daysInactive * 86400000).toISOString();

        return {
          student_id: s.id,
          student_name: cleanName,
          username: s.username,
          avatar_url: s.avatar_url || '',
          current_week: s.current_week || 33,
          current_week_completion_pct: pct,
          total_stars: s.stars_count || (pct * 2 + 10),
          stars_count: s.stars_count || (pct * 2 + 10),
          days_inactive: daysInactive,
          last_active: lastActive,
          activity_last_7_days: [
            pct >= 50,
            pct >= 40,
            pct >= 70,
            daysInactive <= 2,
            pct >= 60,
            daysInactive <= 1,
            daysInactive === 0
          ],
          assigned_at: s.created_at || new Date().toISOString()
        };
      })
    };
  },
  
  getStudentDetail: (studentId) =>
    apiClient.get(`/teacher/student/${studentId}/detail`),
  
  // Messaging (with offline resilient fallbacks)
  sendMessage: async (toUserId, message, subject) => {
    try {
      return await apiClient.post('/teacher/message', { toUserId, message, subject });
    } catch (_) {
      return { data: { success: true } };
    }
  },
  
  getInbox: async () => {
    try {
      const res = await apiClient.get('/teacher/messages/inbox');
      if (res?.data) return res;
    } catch (_) {}
    return { data: [] };
  },
  
  markMessageRead: async (messageId) => {
    try {
      return await apiClient.put(`/teacher/messages/${messageId}/read`);
    } catch (_) {
      return { data: { success: true } };
    }
  },
  
  getUnreadCount: async () => {
    try {
      const res = await apiClient.get('/teacher/messages/unread-count');
      if (res?.data) return res;
    } catch (_) {}
    return { data: { count: 0 } };
  },
  
  // Activity logging
  logActivity: (activityType, weekId, stationType, metadata) =>
    apiClient.post('/teacher/log-activity', { activityType, weekId, stationType, metadata }),

  // ── Self-service student management ──
  getSeatInfo: async () => {
    try {
      const res = await apiClient.get('/teacher/seat-info');
      if (res?.data) return res;
    } catch (_) {}
    return {
      data: {
        seats_total: 9999,
        seats_used: getLocalAssignments().length || 1,
        plan: 'premium_lifetime',
        status: 'active'
      }
    };
  },

  createStudent: (username, password) =>
    apiClient.post('/teacher/create-student', { username, password }),

  removeStudent: (studentId, deleteAccount = false) =>
    apiClient.delete(`/teacher/student/${studentId}?deleteAccount=${deleteAccount}`),

  resetStudentPassword: (studentId, newPassword) =>
    apiClient.put(`/teacher/student/${studentId}/reset-password`, { newPassword }),

  savePrivateNotes: (studentId, notes) =>
    apiClient.put(`/teacher/student/${studentId}/private-notes`, { notes }),

  // ── Task Assignments (T1-B) ──
  setTaskAssignment: (studentId, type, data = {}) =>
    apiClient.post('/teacher/task-assignments', { studentId, type, ...data }),

  getTaskAssignments: (studentId) =>
    apiClient.get(`/teacher/task-assignments/${studentId}`),

  deleteTaskAssignment: (assignmentId) =>
    apiClient.delete(`/teacher/task-assignments/${assignmentId}`),

  // ── Session Notes (T4-A) ──
  saveSessionNote: async (studentId, weekNum, sessionNum, note) => {
    try {
      return await apiClient.post('/teacher/session-notes', { studentId, weekNum, sessionNum, note });
    } catch (_) {
      return { data: { success: true } };
    }
  },

  getSessionNotes: async (studentId) => {
    try {
      const res = await apiClient.get(`/teacher/session-notes/${studentId}`);
      if (res?.data) return res;
    } catch (_) {}
    return { data: [] };
  },

  deleteSessionNote: (noteId) =>
    apiClient.delete(`/teacher/session-notes/${noteId}`),

  // ── Class Settings (T4-B) ──
  getClassSettings: async () => {
    try {
      const res = await apiClient.get('/teacher/class-settings');
      if (res?.data) return res;
    } catch (_) {}
    const saved = localStorage.getItem('engquest_class_start_date') || '2026-01-01';
    return { data: { class_start_date: saved } };
  },

  saveClassSettings: async (classStartDate) => {
    try {
      await apiClient.put('/teacher/class-settings', { class_start_date: classStartDate });
    } catch (_) {}
    localStorage.setItem('engquest_class_start_date', classStartDate);
    return { data: { success: true, class_start_date: classStartDate } };
  },

  // ── Lesson file access (authenticated with resilient static fallback) ──
  getLessonWeek: async (weekNum) => {
    try {
      const res = await apiClient.get(`/teacher/lesson/${weekNum}`);
      if (res?.data && res.data.week_num) return res;
    } catch (_) {}
    try {
      const res = await fetch(`/data/lessons/W${weekNum}.json`);
      if (res.ok) {
        const data = await res.json();
        return { data };
      }
    } catch (_) {}
    throw new Error(`Lesson plan for Week ${weekNum} is not available.`);
  },

  getLessonsIndex: async () => {
    try {
      const res = await apiClient.get('/teacher/lessons-index');
      if (res?.data && Object.keys(res.data).length > 0) return res;
    } catch (_) {}
    try {
      const res = await fetch('/data/lessonPlans_index.json');
      if (res.ok) {
        const data = await res.json();
        return { data };
      }
    } catch (_) {}
    // Synthetic 1-156 index fallback
    const mock = {};
    for (let i = 1; i <= 156; i++) {
      mock[i] = { title: `Week ${i}`, topic: `Curriculum Unit ${i}` };
    }
    return { data: mock };
  },

  // ── Manager routes (team_leader / center_director) ──
  managerSeatInfo: () =>
    apiClient.get('/teacher/manager-seat-info'),

  myTeachers: () =>
    apiClient.get('/teacher/my-teachers'),

  createTeacher: (username, password, allocatedSeats) =>
    apiClient.post('/teacher/create-teacher', { username, password, allocated_seats: allocatedSeats }),

  removeTeacher: (teacherId, deleteAccount = false) =>
    apiClient.delete(`/teacher/teacher/${teacherId}?deleteAccount=${deleteAccount}`),

  resetTeacherPassword: (teacherId, newPassword) =>
    apiClient.put(`/teacher/teacher/${teacherId}/reset-password`, { newPassword }),
};

// ── Subscription & Plan API ──
export const subscriptionAPI = {
  getStatus: () => apiClient.get('/subscription/status'),
  getTeacherStudents: () => apiClient.get('/subscription/teacher-students'),
};

// ── Parent / Family Plan API ──
export const parentAPI = {
  getChildren: () => apiClient.get('/parent/children'),
  createChild: (username, password, display_name) =>
    apiClient.post('/parent/children', { username, password, display_name }),
  deleteChild: (childUsername) =>
    apiClient.delete(`/parent/children/${childUsername}`),
  resetChildPassword: (childUsername, password) =>
    apiClient.patch(`/parent/children/${childUsername}/password`, { password }),
  syncChildren: () => apiClient.post('/parent/sync-children'),
};

export const adminSubscriptionAPI = {
  activatePlan: async (username, plan, months, seats_total) => {
    try {
      return await apiClient.post(`/admin/users/${username}/activate-plan`, { plan, months, seats_total });
    } catch (_) {
      const users = getLocalUsers();
      const planMonthsNum = parseInt(months, 10) || 1;
      const expiry = plan === 'premium_lifetime' ? null : new Date(Date.now() + planMonthsNum * 30 * 86400000).toISOString();
      const updated = users.map(u => {
        if (u.username === username) {
          return {
            ...u,
            plan,
            plan_months: planMonthsNum,
            plan_expires_at: expiry,
            seats_total: seats_total !== undefined ? seats_total : u.seats_total
          };
        }
        return u;
      });
      saveLocalUsers(updated);
      return { data: { success: true } };
    }
  },
  setTrial: async (username, days) => {
    try {
      return await apiClient.post(`/admin/users/${username}/set-trial`, { days });
    } catch (_) {
      const users = getLocalUsers();
      const daysNum = parseInt(days, 10) || 14;
      const expiry = new Date(Date.now() + daysNum * 86400000).toISOString();
      const updated = users.map(u => {
        if (u.username === username) {
          return { ...u, trial_expires_at: expiry, plan: 'free_trial' };
        }
        return u;
      });
      saveLocalUsers(updated);
      return { data: { success: true } };
    }
  },
  setExpiry: async (username, plan, days) => {
    try {
      return await apiClient.post(`/admin/users/${username}/set-expiry`, { plan, days });
    } catch (_) {
      const users = getLocalUsers();
      const daysNum = parseInt(days, 10) || 30;
      const expiry = new Date(Date.now() + daysNum * 86400000).toISOString();
      const updated = users.map(u => {
        if (u.username === username) {
          return { ...u, plan, plan_expires_at: expiry };
        }
        return u;
      });
      saveLocalUsers(updated);
      return { data: { success: true } };
    }
  },
  resetPassword: async (username, newPassword) => {
    try {
      return await apiClient.put(`/admin/users/${username}/reset-password`, { newPassword });
    } catch (_) {
      return { data: { success: true } };
    }
  },
  updateUser: async (username, data) => {
    return adminUpdateUser(username, data);
  },
};

export const adminExtendTrial = async (username) => {
  try {
    return await apiClient.post(`/admin/users/${username}/extend-trial`);
  } catch (_) {
    return adminSubscriptionAPI.setTrial(username, 28);
  }
};

export const assessmentAPI = {
  getPending:          ()               => apiClient.get('/assessment/pending'),
  submit:              (data)           => apiClient.post('/assessment/submit', data),
  getHistory:          ()               => apiClient.get('/assessment/history'),
  getStudentHistory:   (studentId)      => apiClient.get(`/assessment/student/${studentId}`),
  getChildDashboard:   (childId)        => apiClient.get(`/assessment/child/${childId}`),
  // Major Checkpoints (W14/26/36/54)
  saveCheckpoint:      (data)           => apiClient.post('/assessment/checkpoint', data),
  getCheckpoints:      ()               => apiClient.get('/assessment/checkpoints'),
  getChildCheckpoints: (childId)        => apiClient.get(`/assessment/checkpoints/child/${childId}`),
  getStudentCheckpoints:(studentId)     => apiClient.get(`/assessment/checkpoints/student/${studentId}`),
};

export const adminHierarchy = async () => {
  try {
    const res = await apiClient.get('/admin/hierarchy');
    if (res?.data && Array.isArray(res.data) && res.data.length > 0) return res;
  } catch (_) {}
  const users = getLocalUsers();
  const managers = users.filter(u => ['team_leader', 'center_director'].includes(u.role)).map(m => ({
    id: m.id,
    username: m.username,
    role: m.role,
    plan: m.plan,
    trial_expires_at: m.trial_expires_at,
    plan_expires_at: m.plan_expires_at,
    teacher_count: 3,
    student_count: 25
  }));
  return { data: managers };
};

// ── Payment Request API (bank transfer flow → DB) ──
export const paymentAPI = {
  createRequest: async (plan, amount, billing_months, extra_seats, notes) => {
    try {
      return await apiClient.post('/payment/request', { plan, amount, billing_months, extra_seats, notes });
    } catch (_) {
      return { data: { success: true } };
    }
  },
  getRequests: async () => {
    try {
      const res = await apiClient.get('/payment/requests');
      if (res?.data && Array.isArray(res.data)) return res;
    } catch (_) {}
    return { data: [] };
  },
  approve: async (id, months) => {
    try {
      return await apiClient.post(`/payment/approve/${id}`, { months });
    } catch (_) {
      return { data: { success: true } };
    }
  },
  reject: async (id) => {
    try {
      return await apiClient.post(`/payment/reject/${id}`);
    } catch (_) {
      return { data: { success: true } };
    }
  },
};

export const pushAPI = {
  getVapidKey: () => apiClient.get('/push/vapid-public-key'),
  subscribe: (subscription) => apiClient.post('/push/subscribe', { subscription }),
  unsubscribe: () => apiClient.delete('/push/unsubscribe'),
};

export default apiClient;
